'use client';
import { useState, useCallback, useRef, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useLatestRef, useIsomorphicEffect } from '@s7/hooks';
import { deepEqual } from '@s7/utils';
import { computePosition } from './dom/computePosition.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
/**
 * Предоставляет данные для позиционирования плавающего элемента.
 */ function useFloating(options = {}) {
    const { placement = 'bottom', strategy = 'absolute', middleware = [], platform, elements: { reference: externalReference, floating: externalFloating } = {}, whileElementsMounted, open } = options;
    const [data, setData] = useState({
        x: 0,
        y: 0,
        strategy,
        placement,
        middlewareData: {},
        isPositioned: false
    });
    const [latestMiddleware, setLatestMiddleware] = useState(middleware);
    if (!deepEqual(latestMiddleware, middleware)) {
        setLatestMiddleware(middleware);
    }
    const [_reference, _setReference] = useState(null);
    const [_floating, _setFloating] = useState(null);
    const setReference = useCallback((node)=>{
        if (node != referenceRef.current) {
            referenceRef.current = node;
            _setReference(node);
        }
    }, [
        _setReference
    ]);
    const setFloating = useCallback((node)=>{
        if (node !== floatingRef.current) {
            floatingRef.current = node;
            _setFloating(node);
        }
    }, [
        _setFloating
    ]);
    const referenceEl = externalReference || _reference;
    const floatingEl = externalFloating || _floating;
    const referenceRef = useRef(null);
    const floatingRef = useRef(null);
    const dataRef = useRef(data);
    const whileElementsMountedRef = useLatestRef(whileElementsMounted);
    const platformRef = useLatestRef(platform);
    const update = useCallback(()=>{
        if (!referenceRef.current || !floatingRef.current) {
            return;
        }
        const config = {
            placement,
            strategy,
            middleware: latestMiddleware
        };
        if (platformRef.current) {
            config.platform = platformRef.current;
        }
        computePosition(referenceRef.current, floatingRef.current, config).then((data)=>{
            const fullData = _extends({}, data, {
                isPositioned: true
            });
            if (isMountedRef.current && !deepEqual(dataRef.current, fullData)) {
                dataRef.current = fullData;
                flushSync(()=>setData(fullData));
            }
        });
    }, [
        latestMiddleware,
        placement,
        strategy,
        platformRef
    ]);
    useIsomorphicEffect(()=>{
        if (open === false && dataRef.current.isPositioned) {
            dataRef.current.isPositioned = false;
            setData((data)=>_extends({}, data, {
                    isPositioned: false
                }));
        }
    }, [
        open
    ]);
    const isMountedRef = useRef(false);
    useIsomorphicEffect(()=>{
        isMountedRef.current = true;
        return ()=>{
            isMountedRef.current = false;
        };
    }, []);
    useIsomorphicEffect(()=>{
        if (referenceEl) {
            referenceRef.current = referenceEl;
        }
        if (floatingEl) {
            floatingRef.current = floatingEl;
        }
        if (referenceEl && floatingEl) {
            if (whileElementsMountedRef.current) {
                return whileElementsMountedRef.current(referenceEl, floatingEl, update);
            }
            update();
        }
    }, [
        referenceEl,
        floatingEl,
        update,
        whileElementsMountedRef
    ]);
    const refs = useMemo(()=>({
            reference: referenceRef,
            floating: floatingRef,
            setReference,
            setFloating
        }), [
        setReference,
        setFloating
    ]);
    const elements = useMemo(()=>({
            reference: referenceEl,
            floating: floatingEl
        }), [
        referenceEl,
        floatingEl
    ]);
    return useMemo(()=>_extends({}, data, {
            update,
            refs,
            elements
        }), [
        data,
        update,
        refs,
        elements
    ]);
}

export { useFloating };
//# sourceMappingURL=useFloating.js.map
